import { FC, useRef, useState } from 'react';
import styled from '@emotion/styled';
import CallForHelpWinkBox from 'components/call-for-help/CallForHelpWinkBox';
import { maxLenght, required, useForm } from 'components/core/form';
import { Input } from 'components/form/Input';
import { Select, StandardSelectOption } from 'components/form/Select';
import { InsuranceFormModel } from 'components/insurance/InsuranceForm';
import { formatPartialPhoneNumber } from 'helpers/transformers/formatPhoneNumber';

import UploadTag from '../leads/components/UploadTag';

interface InsuranceFormProps {
  initialValues?: Partial<InsuranceFormModel>;
  handleSubmit: (data: InsuranceFormModel) => void;
  hidePayerId?: boolean;
  hideGroupNumber?: boolean;
  hideInsurancePhoneNumber?: boolean;
  hideMemberId?: boolean;
  onChange?: () => void;
  setInsuranceOption: (option: StandardSelectOption | null) => void;
  insuranceOption: StandardSelectOption | null;
  insuranceSelectOptions?: StandardSelectOption[];
}

const Wrapper = styled.div`
  > * {
    padding-bottom: 24px;
  }
`;
const SponsorDivider = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

export const NpInsuranceForm: FC<InsuranceFormProps> = ({
  initialValues,
  handleSubmit,
  hidePayerId,
  hideGroupNumber,
  hideInsurancePhoneNumber,
  hideMemberId,
  onChange,
  setInsuranceOption,
  insuranceOption,
  insuranceSelectOptions = [],
}) => {
  const { registerField, registerSubmitCallback } = useForm<InsuranceFormModel>();
  const frontRef = useRef<HTMLInputElement | null>(null);
  const backRef = useRef<HTMLInputElement | null>(null);
  const [canContinue, setCanContinue] = useState(false);
  const [frontError, setFrontError] = useState('');
  const [backError, setBackError] = useState('');

  const handleFileChange = () => {
    setCanContinue((!!frontRef.current?.files?.length && !!backRef.current?.files?.length) ?? false);
  };

  registerSubmitCallback(async (isValid, data) => {
    if (frontRef.current?.files?.length === 0 && !initialValues?.insuranceCardFrontUrl) {
      if (frontRef.current?.files?.length === 0) {
        setFrontError('Please upload the front of the insurance card.');
      } else {
        setFrontError('');
      }
      isValid = false;
    }
    if (backRef.current?.files?.length === 0 && !initialValues?.insuranceCardBackUrl) {
      if (backRef.current?.files?.length === 0) {
        setBackError('Please upload the back of the insurance card.');
      } else {
        setBackError('');
      }
      isValid = false;
    }
    if (
      frontRef.current?.files?.length === 0 &&
      backRef.current?.files?.length === 0 &&
      initialValues?.insuranceCardFrontUrl &&
      initialValues?.insuranceCardBackUrl
    ) {
      data.leadId = initialValues?.leadId;
    }

    if (isValid) handleSubmit(data);
  });

  return (
    <Wrapper>
      <div className="grid-12 m-grid-6">
        <UploadTag
          className="span-6"
          name="insuranceCardFront"
          label="Front of Card"
          defaultImage={initialValues?.insuranceCardFrontUrl}
          ref={frontRef}
          onChange={handleFileChange}
          removeImage={() => {
            if (!frontRef.current) return;
            frontRef.current.value = '';
          }}
          error={frontError}
        />
        <UploadTag
          className="span-6"
          name="insuranceCardBack"
          defaultImage={initialValues?.insuranceCardBackUrl}
          label="Back of Card"
          ref={backRef}
          onChange={handleFileChange}
          removeImage={() => {
            if (!backRef.current) return;
            backRef.current.value = '';
          }}
          error={backError}
        />
      </div>
      <CallForHelpWinkBox margin="0 0 40px 0" />
      <SponsorDivider>
        <Select
          id="sponsorId"
          name="sponsorId"
          data-testid="sponsorId"
          options={insuranceSelectOptions}
          label="Insurance Provider"
          openMenuOnFocus
          blurInputOnSelect
          classNamePrefix="select"
          value={insuranceOption}
          onChange={setInsuranceOption}
        />
      </SponsorDivider>
      <div className="flex flex-row gap-4 align-items-stretch">
        {!hideMemberId && (
          <Input
            requiredStar
            label="Member ID"
            data-testid="insurance-form-member-id"
            defaultValue={initialValues?.memberId}
            onChange={onChange}
            {...registerField('memberId', {
              validators: [required],
            })}
          />
        )}
        {!hideGroupNumber && (
          <Input
            requiredStar
            label="Group Number"
            data-testid="insurance-form-group-number"
            defaultValue={initialValues?.groupNumber}
            onChange={onChange}
            {...registerField('groupNumber', {
              validators: [required],
            })}
          />
        )}
      </div>
      <div className="flex flex-row gap-4 align-items-stretch">
        {!hideInsurancePhoneNumber && (
          <Input
            requiredStar
            label="Insurance Phone Number"
            data-testid="insurance-form-phone-number"
            placeholder="(XXX) XXX-XXXX"
            defaultValue={initialValues?.phoneNumber}
            onChange={onChange}
            {...registerField('phoneNumber', {
              mask: formatPartialPhoneNumber,
              validators: [required],
            })}
          />
        )}
        {!hidePayerId && (
          <Input
            requiredStar
            label="Payer ID"
            maxLength={5}
            minLength={5}
            data-testid="insurance-form-payer-id"
            defaultValue={initialValues?.payerId}
            onChange={onChange}
            {...registerField('payerId', {
              validators: [required, maxLenght(5, true, 'Payer ID is 5 characters.')],
            })}
          />
        )}
      </div>
    </Wrapper>
  );
};
